/*
 * vizor-contacts.js
 * Vizor konfigurator kontaktnih sočiva — inline formular na PDP-u.
 * Spec: docs/superpowers/specs/2026-08-22-vizor-contacts-design.md
 *
 * Radi tri stvari:
 *   1. prekidač "Različito levo i desno oko" — druga kolona i naslovi
 *   2. cena: unit × (qty × (razlicito ? 2 : 1))
 *   3. dva puta do korpe — AJAX bez fajla, form POST sa fajlom
 *
 * Vrednosti polja dolaze iz Liquid-a (data-vzc-*), JS ih ne poznaje unapred.
 *
 * Modul (ne klasičan skript) da bi mogao da uveze @theme/events — tema sluša
 * CartAddEvent/CartUpdateEvent (event tip 'cart:update'), ne generički CustomEvent.
 * Bez pravog event tipa cart-icon, cart-drawer i cart-items-component ne znaju
 * da se korpa promenila i ne osvežavaju se bez ručnog reload-a.
 */
import { CartAddEvent } from '@theme/events';

(function () {
  'use strict';

  var MAX_FILE = 20 * 1024 * 1024;
  var OK_TYPES = /^image\/|^application\/pdf$/;

  function init(form) {
    if (form.dataset.vzcReady) return;
    form.dataset.vzcReady = '1';

    var diff = form.querySelector('[data-vzc-diff]');
    var grid = form.querySelector('[data-vzc-grid]');
    var qty = form.querySelector('[data-vzc-qty]');
    var file = form.querySelector('[data-vzc-file]');
    var fileLabel = form.querySelector('[data-vzc-filelabel]');
    var fileErr = form.querySelector('[data-vzc-fileerr]');
    var unitRow = form.querySelector('[data-vzc-unitrow]');
    var unitEl = form.querySelector('[data-vzc-unit]');
    var boxesEl = form.querySelector('[data-vzc-boxes]');
    var totalEl = form.querySelector('[data-vzc-total]');
    var idInput = form.querySelector('[data-vzc-id]');
    var postQty = form.querySelector('[data-vzc-postqty]');
    var submit = form.querySelector('[data-vzc-submit]');
    var errEl = form.querySelector('[data-vzc-error]');
    var colR = form.querySelector('[data-vzc-col="R"]');
    var headL = form.querySelector('[data-vzc-colhead="L"]');

    var block = form.closest('.vzc');
    var labels = readLabels(block);
    var price = parseInt(form.dataset.vzcPrice, 10) || 0;
    var defaultUploadText = fileLabel ? fileLabel.textContent : '';
    var defaultCta = submit ? submit.textContent : 'Dodaj u korpu';
    var helpBtn = block ? block.querySelector('[data-vzc-help-toggle]') : null;
    var helpPop = block ? block.querySelector('[data-vzc-help-pop]') : null;
    var helpClose = block ? block.querySelector('[data-vzc-help-close]') : null;

    /* Auto-dopuna (2026-10-01) — postoji samo na sočivima kojima je uključena */
    var ad = form.querySelector('[data-vzc-ad]');
    var adOn = ad ? ad.querySelector('[data-vzc-ad-on]') : null;
    var adToggle = ad ? ad.querySelector('[data-vzc-ad-toggle]') : null;
    var adPanel = ad ? ad.querySelector('[data-vzc-ad-panel]') : null;
    var adRadios = ad ? Array.prototype.slice.call(ad.querySelectorAll('[data-vzc-ad-int]')) : [];
    var adPill = ad ? ad.querySelector('[data-vzc-ad-pill]') : null;
    var adManual = false;   // kupac je sam izabrao interval — procena ga više ne menja

    /* ── cena ── */

    function money(cents) {
      var amount = cents / 100;
      try {
        return new Intl.NumberFormat('sr-RS', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(amount) + ' RSD';
      } catch (e) {
        return Math.round(amount) + ' RSD';
      }
    }

    function boxWord(n) {
      if (n === 1) return 'kutija';
      return n < 5 ? 'kutije' : 'kutija';
    }

    function render() {
      var split = !!(diff && diff.checked);
      var n = parseInt(qty && qty.value, 10) || 1;
      var boxes = n * (split ? 2 : 1);

      if (grid) {
        if (split) grid.setAttribute('data-vzc-split', '');
        else grid.removeAttribute('data-vzc-split');
      }
      if (colR) colR.hidden = !split;
      if (headL) headL.textContent = split ? labels.left : labels.single;

      /* Bez prekidača je total = unit × qty, pa je zaseban red redundantan */
      if (unitRow) unitRow.hidden = !split;
      if (unitEl) unitEl.textContent = money(price);
      if (boxesEl) boxesEl.textContent = boxes + ' ' + boxWord(boxes);
      if (totalEl) totalEl.textContent = money(price * boxes);

      if (postQty) postQty.value = String(n);
      if (adOn && adOn.checked && !adManual) adPickDefault();
    }

    /* ── Auto-dopuna ──
       Interval se ne podrazumeva naslepo: štikliranje bira onaj koji je najbliži tome
       koliko izabrana količina stvarno traje. Mesečna sočiva od 6 komada za oba oka
       traju ~3 meseca — „1 mesec" bi kupcu slao trostruko više nego što mu treba. */

    var AD_DAYS_PER_LENS = { 'Dnevna': 1, 'Dvonedeljna': 14, 'Mesečna': 30 };
    var AD_TYPICAL_PACK  = { 'Dnevna': 30, 'Dvonedeljna': 6, 'Mesečna': 6 };

    function adDays(label) {
      var m = /(\d+(?:[.,]\d+)?)\s*(dan|sedmic|nedelj|mesec)/i.exec(label || '');
      if (!m) return null;
      var n = parseFloat(m[1].replace(',', '.'));
      var u = m[2].toLowerCase();
      return Math.round(n * (u === 'dan' ? 1 : u === 'mesec' ? 30 : 7));
    }

    /* Koliko dana traje izabrana količina: bez prekidača n kutija deli se na dva oka,
       sa prekidačem svako oko dobija svojih n kutija. Veličina pakovanja: naziv izabrane
       varijante („6 sočiva") → polje proizvoda custom.sociva_pakovanje → tipično za režim.
       Bez polja bi kutija od 3 (Biofinity) dobila dvostruko duži interval od trajanja. */
    function adSupplyDays() {
      var rezim = ad.dataset.vzcAdRezim;
      var m = /(\d+)\s*so[čc]/i.exec(ad.dataset.vzcAdVariant || '');
      var own = parseInt(ad.dataset.vzcAdPack, 10);
      var pack = m ? parseInt(m[1], 10) : (own > 0 ? own : (AD_TYPICAL_PACK[rezim] || 6));
      var split = !!(diff && diff.checked);
      var n = parseInt(qty && qty.value, 10) || 1;
      var perEye = split ? n * pack : n * pack / 2;
      return perEye * (AD_DAYS_PER_LENS[rezim] || 30);
    }

    function adPickDefault() {
      if (!adRadios.length) return;
      var target = adSupplyDays(), best = adRadios[0], bestGap = Infinity;
      adRadios.forEach(function (r) {
        var d = adDays(r.value);
        if (!d || !target) return;
        var gap = Math.abs(Math.log(d / target));
        if (gap < bestGap) { bestGap = gap; best = r; }
      });
      best.checked = true;
      adSync();
    }

    function adSetOpen(open) {
      if (!ad) return;
      ad.classList.toggle('is-open', open);
      if (adPanel) adPanel.hidden = !open;
      if (adToggle) adToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /* Stanje trake + tekst pilule u sklopljenoj traci (A2) */
    function adSync() {
      if (!ad) return;
      ad.classList.toggle('is-on', !!(adOn && adOn.checked));
      if (adPill) {
        var c = adChoice();
        adPill.textContent = c ? 'na ' + c.label : '';
      }
    }

    /* Izabran interval, ili null kad pretplata nije uključena */
    function adChoice() {
      if (!adOn || !adOn.checked) return null;
      var r = adRadios.filter(function (x) { return x.checked; })[0];
      return r ? { label: r.value, days: adDays(r.value) } : null;
    }

    /* ── čitanje polja ── */

    function fieldsFor(eye) {
      var out = [];
      var nodes = form.querySelectorAll('[data-vzc-field][data-vzc-eye="' + eye + '"]');
      Array.prototype.forEach.call(nodes, function (el) {
        /* Stepper input već nosi sufiks u vrednosti (normalizacija ga dodaje),
           select i statički div ga dobijaju ovde. */
        if (el.tagName === 'INPUT') {
          if (!el.value) return;
          out.push({ label: el.dataset.vzcLabel, value: el.value });
          return;
        }
        var value = el.tagName === 'SELECT' ? el.value : el.dataset.vzcValue;
        if (!value) return;
        out.push({ label: el.dataset.vzcLabel, value: value + (el.dataset.vzcSuffix || '') });
      });
      return out;
    }

    function propsFor(eye, split, groupId) {
      var props = {};
      if (split) props['Oko'] = eye === 'L' ? 'Levo' : 'Desno';
      fieldsFor(eye).forEach(function (f) { props[f.label] = f.value; });
      /* Rok isporuke (2026-11-04): vrednost proizvoda (custom.delivery_time, inače podrazumevani
         rok teme) upisuje se kao vidljivo svojstvo stavke — stiže u korpu, checkout, potvrdu
         porudžbine, stranicu statusa i Orders u adminu. Snimak u trenutku dodavanja u korpu. */
      if (form.dataset.vzcRok) props['Rok isporuke'] = form.dataset.vzcRok;
      var adc = adChoice();
      if (adc) {
        props['Auto-dopuna'] = 'na ' + adc.label;
        if (adc.days) props['_auto_dopuna_dani'] = String(adc.days);
      }
      props['_vizor_grupa'] = groupId;
      return props;
    }

    /* ── stepper: kretanje kroz listu + validacija upisa ──
       Strelice se pomeraju KROZ listu iz metafielda, ne fiksnim korakom, pa je
       strelicom nemoguće izabrati vrednost koju proizvod nema. Validira se samo
       ručni upis. */

    function stepperParts(box) {
      var input = box.querySelector('.vzc-stepper__in');
      return {
        input: input,
        suffix: input.dataset.vzcSuffix || '',
        values: (input.dataset.vzcValues || '').split('|').filter(Boolean),
        err: box.parentNode.querySelector('[data-vzc-fielderr]')
      };
    }

    /* Prihvata -2,25 · −2.25 · 2,25 · +2.25 (i sa sufiksom) → kanonski oblik iz liste */
    function normalize(raw, suffix) {
      if (raw == null) return null;
      var t = String(raw).trim();
      if (suffix) t = t.split(suffix.trim())[0];
      t = t.replace(/−/g, '-').replace(/\s/g, '').replace(',', '.');
      if (!/^[+-]?\d+(\.\d+)?$/.test(t)) return null;
      return parseFloat(t);
    }

    function toNum(v, suffix) { return normalize(v, suffix); }

    function nearestIn(values, n, suffix) {
      var best = null, bestD = Infinity;
      values.forEach(function (v) {
        var num = toNum(v, suffix);
        if (num === null) return;
        var d = Math.abs(num - n);
        if (d < bestD) { bestD = d; best = v; }
      });
      return best;
    }

    function setStepper(box, value) {
      var p = stepperParts(box);
      p.input.value = value + p.suffix;
      clearFieldErr(box);
      syncStepperBounds(box);
    }

    function clearFieldErr(box) {
      var p = stepperParts(box);
      box.removeAttribute('data-vzc-invalid');
      p.input.removeAttribute('aria-invalid');
      if (p.err) { p.err.hidden = true; p.err.textContent = ''; }
      refreshSubmitState();
    }

    function syncStepperBounds(box) {
      var p = stepperParts(box);
      var idx = p.values.indexOf(p.input.value.replace(p.suffix, ''));
      var dec = box.querySelector('[data-vzc-step="-1"]');
      var inc = box.querySelector('[data-vzc-step="1"]');
      /* Prazno polje (SPH bez podrazumevane vrednosti): obe strelice rade */
      if (!p.input.value.trim()) {
        if (dec) dec.disabled = false;
        if (inc) inc.disabled = false;
        return;
      }
      if (dec) dec.disabled = idx <= 0;
      if (inc) inc.disabled = idx === -1 || idx >= p.values.length - 1;
    }

    function stepBy(box, dir) {
      var p = stepperParts(box);
      var cur = toNum(p.input.value, p.suffix);
      var idx = p.values.indexOf(p.input.value.replace(p.suffix, ''));
      /* Iz praznog polja strelica kreće od vrednosti najbliže nuli u svom smeru
         (−0,25 / +0,25), ne od kraja liste (−12,00 ili +8,00). */
      if (cur === null && !p.input.value.trim()) {
        setStepper(box, nearestIn(p.values, dir < 0 ? -0.25 : 0.25, p.suffix));
        render();
        return;
      }
      if (idx === -1 && cur !== null) idx = p.values.indexOf(nearestIn(p.values, cur, p.suffix));
      if (idx === -1) idx = 0; else idx = Math.max(0, Math.min(p.values.length - 1, idx + dir));
      setStepper(box, p.values[idx]);
      render();
    }

    /* required=true samo pri slanju: prazno polje na blur-u se ne kažnjava
       greškom (kupac možda tek traži recept), ali se ne može poslati. */
    function validateStepper(box, required) {
      var p = stepperParts(box);
      if (!p.input.value.trim()) {
        if (!required) { clearFieldErr(box); syncStepperBounds(box); return true; }
        showFieldErr(box, 'Unesite dioptriju sa recepta ili kutije.', null, 'empty');
        return false;
      }
      var n = toNum(p.input.value, p.suffix);

      if (n === null) {
        showFieldErr(box, 'Unesite broj, npr. ' + (p.values[0] || '').replace('-', '−') + '.', null);
        return false;
      }

      var exact = null;
      p.values.forEach(function (v) { if (toNum(v, p.suffix) === n) exact = v; });
      if (exact !== null) { setStepper(box, exact); return true; }

      var near = nearestIn(p.values, n, p.suffix);
      var lo = toNum(p.values[0], p.suffix);
      var hi = toNum(p.values[p.values.length - 1], p.suffix);
      var msg = (n < Math.min(lo, hi) || n > Math.max(lo, hi))
        ? 'Ovaj proizvod ide od ' + p.values[0] + p.suffix + ' do ' + p.values[p.values.length - 1] + p.suffix + '.'
        : 'Ne pravi se u toj vrednosti.';
      showFieldErr(box, msg, near);
      return false;
    }

    function showFieldErr(box, msg, near, kind) {
      var p = stepperParts(box);
      box.setAttribute('data-vzc-invalid', kind || '');
      p.input.setAttribute('aria-invalid', 'true');
      if (!p.err) return;
      p.err.textContent = '';
      var text = document.createElement('span');
      text.textContent = msg;
      p.err.appendChild(text);
      if (near !== null && near !== undefined) {
        var fix = document.createElement('button');
        fix.type = 'button';
        fix.textContent = 'Uzmi ' + near + p.suffix;
        fix.addEventListener('click', function () { setStepper(box, near); render(); });
        p.err.appendChild(fix);
      }
      p.err.hidden = false;
      refreshSubmitState();
    }

    function visibleSteppers() {
      return Array.prototype.filter.call(form.querySelectorAll('[data-vzc-stepper]'), function (box) {
        var col = box.closest('[data-vzc-col]');
        return !col || !col.hidden;
      });
    }

    function refreshSubmitState() {
      var bad = visibleSteppers().filter(function (b) { return b.hasAttribute('data-vzc-invalid'); });
      if (!submit) return;
      submit.disabled = bad.length > 0;
      var onlyEmpty = bad.every(function (b) { return b.getAttribute('data-vzc-invalid') === 'empty'; });
      submit.textContent = !bad.length ? defaultCta : onlyEmpty ? 'Unesite dioptriju' : 'Ispravite dioptriju';
    }

    /* ── validacija fajla ── */

    function fileProblem() {
      if (!file || !file.files || !file.files.length) return null;
      var f = file.files[0];
      if (!OK_TYPES.test(f.type)) return 'Dozvoljene su slike i PDF dokumenti.';
      if (f.size > MAX_FILE) return 'Fajl je veći od 20 MB.';
      return null;
    }

    function showErr(el, msg) {
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    /* ── korpa ── */

    function addAjax(variantId, quantity, props) {
      return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity, properties: props })
      }).then(function (res) {
        if (!res.ok) throw new Error('cart-add-failed');
        return res.json();
      });
    }

    function hiddenInput(name, value) {
      var el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      el.value = value;
      el.setAttribute('data-vzc-generated', '');
      return el;
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      showErr(errEl, '');

      /* Ručni upis se možda nije validirao (submit bez blur-a) — proveri sve */
      var allOk = true;
      visibleSteppers().forEach(function (box) { if (!validateStepper(box, true)) allOk = false; });
      if (!allOk) {
        var firstBad = visibleSteppers().filter(function (b) { return b.hasAttribute('data-vzc-invalid'); })[0];
        if (firstBad) firstBad.querySelector('.vzc-stepper__in').focus();
        return;
      }

      var problem = fileProblem();
      if (problem) { showErr(fileErr, problem); return; }
      showErr(fileErr, '');

      var split = !!(diff && diff.checked);
      var n = parseInt(qty && qty.value, 10) || 1;
      var variantId = idInput ? idInput.value : form.dataset.vzcVariant;
      var groupId = String(Date.now());
      var hasFile = !!(file && file.files && file.files.length);

      submit.disabled = true;

      /* Bez fajla: sve ide AJAX-om, kupac ostaje na stranici */
      if (!hasFile) {
        var boxesAdded = n * (split ? 2 : 1);
        var calls = [addAjax(variantId, n, propsFor('L', split, groupId))];
        if (split) calls.push(addAjax(variantId, n, propsFor('R', split, groupId)));

        Promise.all(calls).then(function () {
          refreshSubmitState();
          /* Isti event koji product-form.js šalje posle uspešnog dodavanja —
             cart-icon uvećava brojač (source: product-form-component), cart-drawer
             se auto-otvara ako je uključeno u temi, cart-items-component osveži
             sadržaj (bez sections HTML-a sam ponovo povuče sekciju sa servera). */
          document.dispatchEvent(new CartAddEvent({}, form.dataset.vzcProductId || 'vizor-contacts', {
            source: 'product-form-component',
            itemCount: boxesAdded,
            productId: form.dataset.vzcProductId
          }));
        }).catch(function () {
          refreshSubmitState();
          showErr(errEl, 'Dodavanje u korpu nije uspelo. Pokušajte ponovo.');
        });
        return;
      }

      /* Sa fajlom: /cart/add.js ne prima fajlove, pa ide pravi form POST.
         Desno oko mora stići PRE submita — redirect bi prekinuo zahtev u letu. */
      var before = split
        ? addAjax(variantId, n, propsFor('R', split, groupId))
        : Promise.resolve();

      before.then(function () {
        Array.prototype.forEach.call(form.querySelectorAll('[data-vzc-generated]'), function (el) { el.remove(); });
        var props = propsFor('L', split, groupId);
        Object.keys(props).forEach(function (k) {
          form.appendChild(hiddenInput('properties[' + k + ']', props[k]));
        });
        /* name se dodeljuje tek sada — dok stoji u DOM-u, name="id" bi zasenio
           form.id i pokvario uparivanje čvorova u morph.js */
        if (idInput) idInput.name = 'id';
        form.submit();
      }).catch(function () {
        refreshSubmitState();
        showErr(errEl, 'Dodavanje u korpu nije uspelo. Pokušajte ponovo.');
      });
    });

    /* Horizon obično zameni celu sekciju pri promeni varijante, pa nova forma
       dolazi sa tačnom cenom u data-vzc-price. Ovaj hook pokriva slučaj kad
       zamene nema — tada stiže samo novi variant objekat. */
    form.vzcApplyVariant = function (v) {
      if (!v || !v.id) return;
      if (idInput) idInput.value = v.id;
      form.dataset.vzcVariant = v.id;
      if (typeof v.price === 'number') price = v.price;
      if (ad && v.title) ad.dataset.vzcAdVariant = v.title;
      render();
    };

    /* ── vezivanje ── */

    if (diff) diff.addEventListener('change', function () { render(); refreshSubmitState(); });
    if (qty) qty.addEventListener('change', render);

    if (adOn) {
      adOn.addEventListener('change', function () {
        if (adOn.checked) {
          if (!adManual) adPickDefault();
          adSetOpen(false);
        } else {
          adRadios.forEach(function (r) { r.checked = false; });
          adManual = false;
        }
        adSync();   // posle isključivanja pilula se prazni
      });
    }
    adRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        adManual = true;
        if (adOn) adOn.checked = true;
        adSync();
      });
    });
    if (adToggle) adToggle.addEventListener('click', function () { adSetOpen(!ad.classList.contains('is-open')); });

    /* pomoć: klik otvara, klik van njega ili Esc zatvara */
    function setHelp(open) {
      if (!helpPop || !helpBtn) return;
      helpPop.hidden = !open;
      helpBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    if (helpBtn) helpBtn.addEventListener('click', function () { setHelp(helpPop.hidden); });
    if (helpClose) helpClose.addEventListener('click', function () { setHelp(false); });
    document.addEventListener('click', function (ev) {
      if (!helpPop || helpPop.hidden) return;
      if (helpPop.contains(ev.target) || (helpBtn && helpBtn.contains(ev.target))) return;
      setHelp(false);
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') setHelp(false);
    });

    /* stepper */
    Array.prototype.forEach.call(form.querySelectorAll('[data-vzc-stepper]'), function (box) {
      Array.prototype.forEach.call(box.querySelectorAll('[data-vzc-step]'), function (btn) {
        btn.addEventListener('click', function () { stepBy(box, parseInt(btn.dataset.vzcStep, 10)); });
      });
      var input = box.querySelector('.vzc-stepper__in');
      input.addEventListener('input', function () { clearFieldErr(box); });
      input.addEventListener('blur', function () { validateStepper(box); render(); });
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
        if (ev.key === 'ArrowUp') { ev.preventDefault(); stepBy(box, 1); }
        if (ev.key === 'ArrowDown') { ev.preventDefault(); stepBy(box, -1); }
      });
      syncStepperBounds(box);
    });

    if (file) {
      file.addEventListener('change', function () {
        showErr(fileErr, fileProblem());
        if (fileLabel) {
          fileLabel.textContent = file.files && file.files.length
            ? file.files[0].name
            : defaultUploadText;
        }
      });
    }

    render();
  }

  function readLabels(block) {
    return {
      single: (block && block.dataset.vzcSingle) || 'Parametri oka',
      left: (block && block.dataset.vzcLeft) || 'Levo oko'
    };
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-vzc-form]'), init);
    watchWidgets();
  }

  /* Plutajući chat i Wishlist Hero launcher stoje u donjih ~90 px ekrana. Dok forma
     (količina, cena, CTA) prolazi kroz tu traku, klasa na <html> ih sakriva (CSS je
     u bloku, samo za mobilni). Forma se traži iznova — sekcija se menja pri varijanti. */
  var WIDGET_STRIP = 90;
  var widgetsBound = false;
  function watchWidgets() {
    if (widgetsBound) return;
    widgetsBound = true;
    var queued = false;
    function check() {
      queued = false;
      var under = false;
      var vh = window.innerHeight;
      Array.prototype.forEach.call(document.querySelectorAll('.vzc'), function (el) {
        var r = el.getBoundingClientRect();
        if (r.height && r.top < vh && r.bottom > vh - WIDGET_STRIP) under = true;
      });
      document.documentElement.classList.toggle('vzc-under-widgets', under);
    }
    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(check);
    }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    queue();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Jedan listener za ceo dokument, ne po formi — sekcija se pri promeni
     varijante može zameniti, pa boot() hvata novu formu. */
  document.addEventListener('variant:update', function (ev) {
    setTimeout(function () {
      boot();
      var v = ev.detail && ev.detail.resource;
      Array.prototype.forEach.call(document.querySelectorAll('[data-vzc-form]'), function (f) {
        if (f.vzcApplyVariant) f.vzcApplyVariant(v);
      });
    }, 0);
  });
})();
